import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPayment,
  getPaymentSumByMissionId,
} from "../../../store/payment";
import { getCurrentUserData } from "../../../store/users";
import { getGroupById, updateGroup } from "../../../store/groups";
import { loadPlay } from "../../../store/play";
import config from "../../../config";
import ModalShell from "./ModalShell";
import { useI18n } from "../../../i18n/LanguageContext";

const PAYMENT_SUM = Number(config.REACT_APP_PAYMENT) || 0;

function PaymentFormModal({ paymentForm, missionId }) {
  const dispatch = useDispatch();

  const currentUser = useSelector(getCurrentUserData());
  const group = useSelector(getGroupById(currentUser?.teamId));
  const missionPayment = useSelector(getPaymentSumByMissionId(missionId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const closeRef = useRef(() => {});
  const { t } = useI18n();

  const paid = Number(missionPayment) || 0;
  const missionStarted = Boolean(group?.missionId);
  const canPay =
    Boolean(currentUser && group) &&
    !missionStarted &&
    !submitting &&
    PAYMENT_SUM > 0;

  const closeModal = () => closeRef.current();

  const onPayment = async () => {
    if (!canPay) return;

    setSubmitting(true);
    setError("");

    if (paid < PAYMENT_SUM) {
      const created = await dispatch(
        createPayment({
          payer: currentUser._id,
          sum: PAYMENT_SUM,
          missionId,
          teamId: currentUser.teamId,
        })
      );

      if (!created) {
        setSubmitting(false);
        setError(t("pay.fail"));
        return;
      }
    }

    const started = await dispatch(updateGroup({ ...group, missionId }));
    if (!started || !started.missionId) {
      setSubmitting(false);
      setError(t("pay.fail"));
      return;
    }

    await dispatch(loadPlay());
    closeModal();
  };

  return (
    <ModalShell
      panelClassName="max-w-lg sm:max-w-2xl"
      onClose={() => paymentForm(null)}
      closeRef={closeRef}
    >
      <div className="relative flex w-full min-w-0 flex-col rounded-lg border-0 bg-white shadow-lg outline-none">
        <div className="flex items-start justify-between gap-3 rounded-t border-b border-solid border-slate-200 p-4 sm:p-5">
          <h3 className="min-w-0 text-xl font-semibold sm:text-3xl">
            {t("pay.title")}
          </h3>
          <button
            className="btn-smooth shrink-0 border-0 bg-transparent p-1 text-2xl font-semibold leading-none text-black outline-none"
            onClick={closeModal}
            type="button"
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
        <div className="relative flex-auto p-4 sm:p-6">
          <p className="text-base leading-relaxed text-slate-500 sm:text-lg">
            {t("pay.text")}
          </p>
        </div>
        <div className="flex px-4 sm:px-6">
          <p>{t("pay.paid")}</p>
          <p className="pl-2 text-slate-500">{paid}</p>
        </div>

        <form
          className="w-full"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="m-4 mb-6 sm:m-8 md:flex md:items-center">
            <label
              className="mb-1 block pr-0 font-bold text-gray-500 md:mb-0 md:w-1/3 md:pr-6 md:text-right"
              htmlFor="payment-sum"
            >
              {t("pay.sum")}
            </label>
            <div className="md:w-2/3">
              <input
                id="payment-sum"
                className="w-full appearance-none rounded border-2 border-gray-200 bg-gray-200 px-4 py-2 leading-tight text-gray-700 focus:border-purple-500 focus:bg-white focus:outline-none"
                name="sum"
                type="number"
                value={PAYMENT_SUM}
                readOnly
              />
            </div>
          </div>
        </form>
        {error ? (
          <p className="px-4 pb-2 text-sm text-red-500 sm:px-6">{error}</p>
        ) : null}
        {!group ? (
          <p className="px-4 pb-2 text-sm text-red-500 sm:px-6">
            {t("pay.noTeam")}
          </p>
        ) : null}
        {missionStarted ? (
          <p className="px-4 pb-2 text-sm text-slate-500 sm:px-6">
            {t("pay.started")}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 rounded-b border-t border-solid border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-end sm:p-6">
          <button
            className="btn-smooth w-full px-6 py-2 text-sm font-bold uppercase text-red-500 outline-none sm:w-auto"
            type="button"
            onClick={closeModal}
          >
            {t("common.close")}
          </button>
          <button
            className="btn-smooth w-full rounded bg-emerald-500 px-6 py-3 text-sm font-bold uppercase text-white shadow outline-none hover:bg-emerald-600 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            type="button"
            onClick={onPayment}
            disabled={!canPay}
          >
            {submitting ? t("pay.sending") : t("pay.send")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export default PaymentFormModal;
